import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import type { FinanceCustomerDto } from '@thebb/shared';
import { get } from '../api';

/**
 * Picks one of the customers under Økonomi (the debtor an invoice goes to) by name or number.
 * Without access to Økonomi the search fails, and the field falls back to typing the number.
 */
export function FinanceCustomerPicker({ value, onChange, label = 'Faktureres til (Økonomi-kunde)' }: { value: string | null; onChange: (number: string | null) => void; label?: string }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);
  const current = useQuery({ queryKey: ['finance-customer', value], queryFn: () => get<FinanceCustomerDto[]>(`/finance/customers?q=${encodeURIComponent(value!)}`), enabled: !!value, retry: false });
  const results = useQuery({ queryKey: ['finance-customers', debounced], queryFn: () => get<FinanceCustomerDto[]>(`/finance/customers?q=${encodeURIComponent(debounced)}`), enabled: open && debounced.length >= 2, retry: false });
  const chosen = current.data?.find((c) => c.number === value) ?? null;

  if (current.isError || results.isError) {
    return (
      <label className="block">
        <span className="label">{label} (kundenr.)</span>
        <input className="input" value={value ?? ''} onChange={(e) => onChange(e.target.value.trim() || null)} />
      </label>
    );
  }
  return (
    <div className="relative">
      <span className="label">{label}</span>
      {value ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-line px-3 py-2 text-sm">
          <span className="min-w-0 truncate">
            {chosen ? <>{chosen.name} <span className="text-muted">· {chosen.number}{chosen.city ? ` · ${chosen.city}` : ''}</span></> : <span className="text-muted">{current.isLoading ? '…' : `${value} (ukendt)`}</span>}
          </span>
          <button type="button" className="text-muted hover:text-ink" onClick={() => onChange(null)} aria-label="Fjern"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <input className="input" placeholder="Søg navn eller kundenr." value={q} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} />
      )}
      {open && !value && debounced.length >= 2 && (
        <div className="absolute left-0 right-0 z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-line bg-white shadow-lg">
          {(results.data ?? []).slice(0, 30).map((c) => (
            <button key={c.id} type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas" onMouseDown={(e) => { e.preventDefault(); onChange(c.number); setQ(''); setOpen(false); }}>
              {c.name} <span className="text-muted">· {c.number}{c.city ? ` · ${c.city}` : ''}{c.vatNumber ? ` · CVR ${c.vatNumber}` : ''}</span>
            </button>
          ))}
          {results.data && results.data.length === 0 && <div className="px-3 py-2 text-sm text-muted">Ingen kunder under Økonomi matcher "{debounced}"</div>}
          {results.isLoading && <div className="px-3 py-2 text-sm text-muted">…</div>}
        </div>
      )}
    </div>
  );
}
